import {
  decodeHtmlEntities,
  displayTitleFromExport,
  extractNotionIdFromFilename,
  normalizeExportPath,
} from './parse-filename.js';
import type { NotionExportNode, NotionExportWorkspace } from './types.js';

function parseWorkspaceMeta(html: string): {
  workspaceId: string | null;
  workspaceName: string | null;
} {
  const idMatch = html.match(/Workspace identifier:\s*([0-9a-f-]{36})/i);
  const nameMatch = html.match(/Workspace name:\s*([^<]+)/i);
  return {
    workspaceId: idMatch?.[1]?.trim() ?? null,
    workspaceName: nameMatch?.[1]?.trim().replace(/&amp;/g, '&') ?? null,
  };
}

function findMatchingCloseUl(html: string, openEnd: number): number {
  const ulOpen = /<ul\b/gi;
  const ulClose = /<\/ul>/gi;
  let depth = 1;
  let pos = openEnd;
  while (depth > 0 && pos < html.length) {
    ulOpen.lastIndex = pos;
    ulClose.lastIndex = pos;
    const nextOpen = ulOpen.exec(html);
    const nextClose = ulClose.exec(html);
    if (!nextClose) {
      return html.length;
    }
    if (nextOpen && nextOpen.index < nextClose.index) {
      depth++;
      pos = nextOpen.index + nextOpen[0].length;
    } else {
      depth--;
      pos = nextClose.index + nextClose[0].length;
      if (depth === 0) {
        return nextClose.index;
      }
    }
  }
  return html.length;
}

/** Strip the trailing `.csv` from a collection-pointer id (`id::{uuid}.csv`). */
function normalizeNotionId(rawId: string): { id: string | null; isCsvPointer: boolean } {
  const trimmed = rawId.trim();
  if (!trimmed) {
    return { id: null, isCsvPointer: false };
  }
  if (/\.csv$/i.test(trimmed)) {
    return { id: trimmed.replace(/\.csv$/i, ''), isCsvPointer: true };
  }
  return { id: trimmed, isCsvPointer: false };
}

type RawBlock = {
  rawId: string;
  start: number;
  end: number;
  innerStart: number;
  innerEnd: number;
};

/** Find immediate child `<ul id="id::...">` blocks within [innerStart, innerEnd). */
function findChildBlocks(html: string, innerStart: number, innerEnd: number): RawBlock[] {
  const blocks: RawBlock[] = [];
  let searchFrom = innerStart;
  while (searchFrom < innerEnd) {
    const rel = html.indexOf('<ul id="id::', searchFrom);
    if (rel < 0 || rel >= innerEnd) {
      break;
    }
    const openMatch = html.slice(rel).match(/^<ul\s+id="id::([^"]*)"/i);
    if (!openMatch) {
      break;
    }
    const gt = html.indexOf('>', rel + openMatch[0].length);
    if (gt < 0) {
      break;
    }
    const blockInnerStart = gt + 1;
    const blockInnerEnd = findMatchingCloseUl(html, blockInnerStart);
    blocks.push({
      rawId: openMatch[1]!,
      start: rel,
      end: blockInnerEnd + '</ul>'.length,
      innerStart: blockInnerStart,
      innerEnd: blockInnerEnd,
    });
    searchFrom = blockInnerEnd + '</ul>'.length;
  }
  return blocks;
}

/** Parse a level of sibling blocks; csv-pointer siblings mark same-id blocks as databases. */
function parseLevel(html: string, blocks: RawBlock[]): NotionExportNode[] {
  const databaseIds = new Set<string>();
  for (const block of blocks) {
    const { id, isCsvPointer } = normalizeNotionId(block.rawId);
    if (isCsvPointer && id) {
      databaseIds.add(id);
    }
  }

  const nodes: NotionExportNode[] = [];
  for (const block of blocks) {
    const { id, isCsvPointer } = normalizeNotionId(block.rawId);
    if (isCsvPointer) {
      continue; // collection pointer; the sibling collection node carries the data
    }

    const childBlocks = findChildBlocks(html, block.innerStart, block.innerEnd);
    const children = parseLevel(html, childBlocks);

    // The node's own anchor is the head of its inner HTML, before any child <ul>.
    const headEnd = childBlocks.length > 0 ? childBlocks[0]!.start : block.innerEnd;
    const head = html.slice(block.innerStart, headEnd);
    const anchorMatch = head.match(/<a(?:\s+href="([^"]*)")?\s*>([^<]*)<\/a>/i);
    const rawHref = decodeHtmlEntities(anchorMatch?.[1] ?? '');
    const anchorText = decodeHtmlEntities(anchorMatch?.[2]?.trim() ?? '');
    const href = /\.md$/i.test(rawHref) ? normalizeExportPath(rawHref) : '';

    const isInlineDatabase = /\(inline database\)/i.test(anchorText);
    const isDatabase = (id != null && databaseIds.has(id)) || isInlineDatabase;

    const idFromHref = href ? extractNotionIdFromFilename(href) : null;
    const notionId = id ?? idFromHref;

    nodes.push({
      notionId,
      title: displayTitleFromExport(anchorText, href),
      href,
      kind: isDatabase ? 'database' : 'page',
      children,
    });
  }
  return nodes;
}

export function parseNotionIndexHtml(html: string): NotionExportWorkspace {
  const meta = parseWorkspaceMeta(html);
  const firstId = html.search(/<ul\s+id="id::/i);
  if (firstId < 0) {
    return { ...meta, roots: [] };
  }

  // Notion wraps everything in a single workspace container <ul id="id::{workspaceId}">
  // whose direct children are the top-level pages/databases.
  const openMatch = html.slice(firstId).match(/^<ul\s+id="id::[^"]*"/i);
  const innerStart = openMatch
    ? html.indexOf('>', firstId + openMatch[0].length) + 1
    : html.indexOf('>', firstId) + 1;
  const innerEnd = findMatchingCloseUl(html, innerStart);

  const childBlocks = findChildBlocks(html, innerStart, innerEnd);
  const roots = parseLevel(html, childBlocks);

  return { ...meta, roots };
}
